import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { createPortal } from 'react-dom';
import {
    DateRange,
    DayButton as DefaultDayButton,
    DayButtonProps,
    DayPicker,
    Matcher
} from 'react-day-picker';
import 'react-day-picker/style.css';
import classes from './InlineDatePicker.module.scss';

export interface Period {
    from: Moment;
    to: Moment;
}

interface DatePickerBaseProps {
    minDate?: Moment;
    maxDate?: Moment;
    timeZone: string;
    className?: string;
    isDateDisabled?: (date: Date) => boolean;
    getDisabledDateTooltip?: (date: Date) => string | undefined;
}

interface DatePickerPropsRange extends DatePickerBaseProps {
    rangeMode: true;
    multipleMode?: false;
    allowPartialRange?: boolean;
    value?: Period | null;
    onChange?: (value: Period | null) => void;
}

interface DatePickerPropsMultiple extends DatePickerBaseProps {
    rangeMode?: false;
    multipleMode: true;
    /** Maximum number of days that can be selected. */
    max?: number;
    value?: Array<Moment> | null;
    onChange?: (value: Array<Moment>) => void;
}

interface DatePickerPropsSingle extends DatePickerBaseProps {
    rangeMode?: false;
    multipleMode?: false;
    value?: Moment | null;
    onChange?: (value: Moment | null) => void;
}

type InlineDatePickerProps = DatePickerPropsRange | DatePickerPropsMultiple | DatePickerPropsSingle;

const momentToLocalDate = (m: Moment): Date => new Date(m.year(), m.month(), m.date());

const localDateToMoment = (date: Date, timeZone: string): Moment => moment(date).tz(timeZone, true);

interface TooltipDayButtonProps extends DayButtonProps {
    tooltip?: string;
}

const TooltipDayButton: React.FC<TooltipDayButtonProps> = ({
    tooltip,
    day,
    modifiers,
    disabled,
    onClick,
    ...rest
}) => {
    const [tooltipPos, setTooltipPos] = React.useState<{ top: number; left: number } | null>(null);
    const isDisabled = modifiers.disabled === true || disabled === true;

    React.useEffect(() => {
        if (!tooltipPos) return;
        const hide = () => setTooltipPos(null);
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [tooltipPos]);

    const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!tooltip) return;
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltipPos({ top: rect.top, left: rect.left + rect.width / 2 });
    };

    const handleMouseLeave = () => {
        if (tooltip) setTooltipPos(null);
    };

    return (
        <>
            <DefaultDayButton
                {...rest}
                day={day}
                modifiers={modifiers}
                disabled={false}
                aria-disabled={isDisabled || undefined}
                onClick={isDisabled ? undefined : onClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={clsx(rest.className, isDisabled && classes.dayDisabled)}
            >
                {day.date.getDate()}
            </DefaultDayButton>
            {tooltip && tooltipPos
                ? createPortal(
                      <div
                          role="tooltip"
                          className={classes.dayTooltip}
                          style={{ top: tooltipPos.top, left: tooltipPos.left }}
                      >
                          {tooltip}
                      </div>,
                      document.body
                  )
                : null}
        </>
    );
};

export const InlineDatePicker: React.FC<InlineDatePickerProps> = props => {
    const {
        minDate,
        maxDate,
        value,
        rangeMode,
        multipleMode,
        timeZone,
        className,
        isDateDisabled,
        getDisabledDateTooltip,
        onChange
    } = props;
    const max = 'max' in props ? props.max : undefined;
    const disabledMatchers = React.useMemo<Array<Matcher>>(() => {
        const matchers: Array<Matcher> = [];

        if (minDate) {
            matchers.push({ before: momentToLocalDate(minDate) });
        }

        if (maxDate) {
            matchers.push({ after: momentToLocalDate(maxDate) });
        }

        if (isDateDisabled) {
            matchers.push((date: Date) => isDateDisabled(date));
        }

        return matchers;
    }, [minDate, maxDate, isDateDisabled]);

    const tooltipDayButton = React.useCallback(
        (props: DayButtonProps) => (
            <TooltipDayButton {...props} tooltip={getDisabledDateTooltip?.(props.day.date)} />
        ),
        [getDisabledDateTooltip]
    );

    const rangeValue = rangeMode ? (value as Period | null | undefined) : undefined;
    const multipleValue = multipleMode ? (value as Array<Moment> | null | undefined) : undefined;
    const singleValue =
        !rangeMode && !multipleMode ? (value as Moment | null | undefined) : undefined;

    const singleSelected = singleValue ? momentToLocalDate(singleValue) : undefined;

    const multipleSelected = React.useMemo(
        () => multipleValue?.map(momentToLocalDate),
        [multipleValue]
    );

    const [partialRange, setPartialRange] = React.useState<DateRange | undefined>(() =>
        rangeValue
            ? { from: momentToLocalDate(rangeValue.from), to: momentToLocalDate(rangeValue.to) }
            : undefined
    );

    React.useEffect(() => {
        if (!rangeMode) return;

        if (!rangeValue) {
            setPartialRange(undefined);
            return;
        }

        setPartialRange({
            from: momentToLocalDate(rangeValue.from),
            to: momentToLocalDate(rangeValue.to)
        });
    }, [rangeValue, rangeMode]);

    const handleSingleSelect = (selected: Date | undefined) => {
        if (!selected) {
            (onChange as DatePickerPropsSingle['onChange'])?.(null);
            return;
        }

        (onChange as DatePickerPropsSingle['onChange'])?.(localDateToMoment(selected, timeZone));
    };

    const handleMultipleSelect = (selected: Array<Date> | undefined) => {
        const dates = (selected ?? [])
            .map(date => localDateToMoment(date, timeZone))
            .sort((a, b) => a.valueOf() - b.valueOf());

        (onChange as DatePickerPropsMultiple['onChange'])?.(dates);
    };

    const handleRangeSelect = (selected: DateRange | undefined) => {
        setPartialRange(selected);

        if (!selected?.from) {
            (onChange as DatePickerPropsRange['onChange'])?.(null);
            return;
        }

        if (!selected.to) {
            return;
        }

        const fromMoment = localDateToMoment(selected.from, timeZone);
        const toMoment = localDateToMoment(selected.to, timeZone);

        (onChange as DatePickerPropsRange['onChange'])?.({ from: fromMoment, to: toMoment });
    };

    const sharedProps = {
        showOutsideDays: false,
        navLayout: 'around' as const,
        disabled: disabledMatchers,
        excludeDisabled: true,
        startMonth: minDate ? momentToLocalDate(minDate) : undefined,
        endMonth: maxDate ? momentToLocalDate(maxDate) : undefined,
        formatters: {
            formatWeekdayName: (date: Date) =>
                date.toLocaleString(undefined, { weekday: 'narrow' }),
            formatCaption: (date: Date) =>
                date.toLocaleString(undefined, { month: 'short', year: 'numeric' })
        },
        modifiersClassNames: {
            selected: classes.modSelected,
            range_start: classes.modRangeStart,
            range_middle: classes.modRangeMiddle,
            range_end: classes.modRangeEnd
        },
        components: {
            DayButton: tooltipDayButton,
            Chevron: (chevronProps: { orientation?: 'up' | 'down' | 'left' | 'right' }) => (
                <FontAwesomeIcon
                    icon={chevronProps.orientation === 'right' ? faAngleRight : faAngleLeft}
                />
            )
        },
        className: clsx(classes.root, className)
    } as const;

    if (rangeMode) {
        return (
            <DayPicker
                {...sharedProps}
                mode="range"
                selected={partialRange}
                onSelect={handleRangeSelect}
            />
        );
    }

    if (multipleMode) {
        return (
            <DayPicker
                {...sharedProps}
                mode="multiple"
                max={max}
                selected={multipleSelected}
                onSelect={handleMultipleSelect}
            />
        );
    }

    return (
        <DayPicker
            {...sharedProps}
            mode="single"
            selected={singleSelected}
            onSelect={handleSingleSelect}
        />
    );
};
